// src/components/ErrorBoundary.jsx
// Global error boundary with Sentry integration and user-friendly fallback UI

import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * Fallback UI shown when an error occurs
 */
const ErrorFallback = ({ error, resetError }) => {
  const handleReload = () => {
    // #region agent log
    // Ingest disabled: 127.0.0.1 is blocked by CSP in prod
    // #endregion
    // Clear any potentially corrupted state
    try {
      sessionStorage.clear();
    } catch (e) {
      // Ignore storage errors
    }
    window.location.reload();
  };

  const handleReset = () => {
    if (resetError) {
      resetError();
    } else {
      handleReload();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8fafc',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        maxWidth: '500px',
        textAlign: 'center',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '40px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          backgroundColor: '#fef2f2',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1 style={{
          color: '#1e293b',
          fontSize: '24px',
          fontWeight: '600',
          margin: '0 0 12px',
        }}>
          Ups, coś poszło nie tak
        </h1>

        <p style={{
          color: '#64748b',
          fontSize: '16px',
          lineHeight: '1.6',
          margin: '0 0 24px',
        }}>
          Przepraszamy za utrudnienia. Nasz zespół został powiadomiony o problemie.
        </p>

        {import.meta.env.DEV && error && (
          <details style={{
            textAlign: 'left',
            marginBottom: '24px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            padding: '12px',
          }}>
            <summary style={{
              cursor: 'pointer',
              color: '#991b1b',
              fontWeight: '500',
              fontSize: '14px',
            }}>
              Szczegóły błędu (tylko w trybie deweloperskim)
            </summary>
            <pre style={{
              marginTop: '12px',
              fontSize: '12px',
              color: '#991b1b',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'auto',
              maxHeight: '200px',
            }}>
              {error.toString()}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={handleReset}
            style={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Spróbuj ponownie
          </button>

          <button
            onClick={handleReload}
            style={{
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#e2e8f0'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#f1f5f9'}
          >
            Odśwież stronę
          </button>
        </div>

        <p style={{
          color: '#94a3b8',
          fontSize: '14px',
          marginTop: '24px',
        }}>
          Jeśli problem się powtarza, skontaktuj się z nami.
        </p>
      </div>
    </div>
  );
};

/**
 * Error Boundary component with Sentry integration
 * Catches JavaScript errors anywhere in the child component tree
 */
const ErrorBoundary = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
      onError={(error, componentStack, eventId) => {
        // Additional logging in development
        if (import.meta.env.DEV) {
          console.error('[ErrorBoundary] Caught error:', error);
          console.error('[ErrorBoundary] Component stack:', componentStack);
          console.error('[ErrorBoundary] Sentry event ID:', eventId);
        }
      }}
      showDialog={false} // Don't show Sentry's default dialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
